import { Alert, Box, Button, CircularProgress, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, Switch, Table, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material"
import { FC, useCallback, useEffect, useState } from "react"
import useGetRemediationProgress from "../../../hooks/useGetRemediationProgress";
import { useGetMultiCloudwatchStateQuery, useStartMultiCloudwatchMutation } from "../../../generated";
import * as yup from "yup";
import { useFormik } from "formik";
import useSnackBars from "../../../hooks/useSnackbar";
import useAccount from "../../../hooks/useAccount";
import CloudwatchSwitches from "../../common/multi-input-remediation-dialogs/CloudwatchSwitches";
import Loader from "../../common/Loader";
import InfoIcon from "@mui/icons-material/Info";
import Progress from "../../common/multi-input-remediation-dialogs/Progress";
import LoadingButton from "@mui/lab/LoadingButton";
import Account from "../../../models/Account";
import MultiInputControls from "../../../models/MultiInputControls";

interface MultiInputCloudwatchRemediateProps {
	arn: string;
	changeTab: () => void;
}

interface FormData {
  trails: Array<Trail>;
  topics: Array<Topic>;
}

const validationSchema = yup.object({
	cloudtrailArn: yup.string().required("CloudTrail Arn is required"),
	loggroupArn: yup.string().required("Logging Group Arn is required"),
	snsTopicArn: yup.string().required("SNS topic Arn is required"),
	snsSubscriptionEmail: yup.string().email("Invalid email address"),
	cloudwatchControls: yup
		.array(yup.string())
		.required()
		.min(1, "At least one cloudwatch control is required"),
	cloudtrail1Finding: yup.string(),
	cloudtrail2Finding: yup.string(),
	cloudtrail3Finding: yup.string(),
	cloudtrail4Finding: yup.string(),
	cloudtrail5Finding: yup.string(),
});

interface FindingControl {
	controlName: string;
	findingId: string;
	status: string;
}

interface FindingInfo {
	controlDetails: Array<FindingControl>;
}

interface Trail {
	trailArn: string;
	name: string;
	cloudwatchLogGroupArn: string;
	findingInfo: FindingInfo;
}

interface Topic {
	topicArn: string;
	topicName: string;
}

/**
 * Display CloudWatch 1-14 remediation workflow
 * @param arn arn of the current account selected
 * @param changeTab tab to switch to after this page is completed
 * @returns 
 */
const MultiInputCloudwatchRemediate: FC<MultiInputCloudwatchRemediateProps> = ({
	arn,
	changeTab
}: MultiInputCloudwatchRemediateProps) => {
	const { accounts, defaultregion } = useAccount();
	const { setAlerts } = useSnackBars();
	const [remediating, setRemediating] = useState(false);
	const [error, setError] = useState("");
	const [managementArn, setManagementArn] = useState(arn); //analyze management arn
	const [region, setRegion] = useState(defaultregion);
	const [findingStatuses, setFindingStatuses] = useState<Record<string, string>>({});
	const [disabled, setDisabled] = useState(false);	
	const [selectAll, setSelectAll] = useState(false);

	const { remediationProgress, remediationMessage, setExecutionArn } =
		useGetRemediationProgress({
			arn: managementArn,
		});
	const [formData, setFormData] = useState<FormData>({
		trails: [],
		topics: [],
	});
	const { data, loading, refetch } = useGetMultiCloudwatchStateQuery({
		variables: {
			arn: managementArn,
		},
		context: {
			apiName: "automation_approval",
		},
		fetchPolicy: "no-cache",
		notifyOnNetworkStatusChange: true,
	});

	const [startMultiCloudwatchMutation] = useStartMultiCloudwatchMutation();

	const handleConfirm = (status: boolean) => {
    if (status) {
			changeTab()
      setAlerts([
        {
          severity: "success",
          msg: "Multi CloudWatch input remediation is successful. Pls, check your email for SNS topic subscription confirmation.",
        },
      ]);
    } else {
      setAlerts([
        {
          severity: "error",
          msg: "Multi CloudWatch input remediation failed.",
        },
      ]);
    }
  };
	
	const formik = useFormik({
		initialValues: {
			cloudtrailArn: "",
			loggroupArn: "",
			snsTopicArn: "",
			snsSubscriptionEmail: "",
			cloudwatchControls: [],
			cloudtrail1Finding: "no_finding",
			cloudtrail2Finding: "no_finding",
			cloudtrail3Finding: "no_finding",
			cloudtrail4Finding: "no_finding",
			cloudtrail5Finding: "no_finding",
		},
		validationSchema,
		validateOnChange: false,
		onSubmit: (formValues) => {
			formik.setSubmitting(true);
			setRemediating(true);
			const values = {
				...formValues,
			};
			const awsAccountRef = managementArn.split(":")[4];
			if (values.cloudtrailArn === "create_new_cloudtrail") {
				if (formData.trails.length === 1) {
					formData.trails[0].findingInfo.controlDetails?.forEach(
						(control: FindingControl) => {
							if (control.controlName === "CloudTrail.1") {
								values.cloudtrail1Finding = control.findingId;
							}
						}
					);
				} else {
					formData.trails.forEach((trail: Trail) => {
						if (trail?.name && trail?.name.includes(awsAccountRef)) {
							formik.setErrors({
								cloudtrailArn:
									"A Cloudtrail already exists for this account. Please select the existing Cloudtrail",
							});
						}
					});
					formik.setSubmitting(false);
					return;
				}
			} else {
				const trail = formData.trails.find(
					(trail: Trail) => trail.trailArn === values.cloudtrailArn
				);
				if (!trail) {
					formik.setFieldError(
						"cloudtrailArn",
						"Selected Cloudtrail is not available"
					);
					formik.setSubmitting(false);
					return;
				}
				if (
					values.loggroupArn !== "use_sixpillars_loggroup" &&
					!trail.cloudwatchLogGroupArn.includes(awsAccountRef)
				) {
					const logGroupAwsAccountRef =
						trail.cloudwatchLogGroupArn.split(":")[4];
					formik.setFieldError(
						"cloudtrailArn",
						`Selected Multi-region Cloudtrail's log group is not available in the A+ deployed AWS account(${awsAccountRef}). Please deploy A+ in the account where the Cloudwatch log group is available (${logGroupAwsAccountRef}).`
					);
					formik.setSubmitting(false);
					return;
				}

				if (
					values.loggroupArn !== "use_sixpillars_loggroup" &&
					!trail.cloudwatchLogGroupArn.includes(region)
				) {
					const logGroupAwsAccountRef =
						trail.cloudwatchLogGroupArn.split(":")[4];
					formik.setFieldError(
						"cloudtrailArn",
						`Selected Multi-region Cloudtrail's log group is not available in the A+ deployed AWS account(${awsAccountRef}) - ${region}. Please deploy A+ in the account where the Cloudwatch log group is available (${logGroupAwsAccountRef}).`
					);
					formik.setSubmitting(false);
					return;
				}
				trail?.findingInfo?.controlDetails?.forEach((control: FindingControl) => {
					if (
						control.controlName === "CloudTrail.1" &&
						values.cloudtrailArn === "create_new_cloudtrail"
					) {
						values.cloudtrail1Finding = control.findingId;
					}

					if (control.status === "FAILED") {
						if (control.controlName === "CloudTrail.2") {
							values.cloudtrail2Finding = control.findingId;
						}

						if (control.controlName === "CloudTrail.4") {
							values.cloudtrail4Finding = control.findingId;
						}
					}
				});
			}
			startMultiCloudwatchMutation({
				variables: {
					arn: managementArn,
					input: {
						cloudtrailArn: values.cloudtrailArn,
						loggroupArn: values.loggroupArn,
						snsTopicArn: values.snsTopicArn,
						snsSubscriptionEmail: values.snsSubscriptionEmail,
						cloudwatchControls: values.cloudwatchControls,
						cloudtrail1Finding: values.cloudtrail1Finding,
						cloudtrail2Finding: values.cloudtrail2Finding,
						cloudtrail3Finding: values.cloudtrail3Finding,
						cloudtrail4Finding: values.cloudtrail4Finding,
						cloudtrail5Finding: values.cloudtrail5Finding,
					},
				},
				context: {
					apiName: "automation_approval",
				},
				notifyOnNetworkStatusChange: true,
			}).then((res) => {
				formik.setSubmitting(false);

				if (res.data?.startMultiCloudwatch?.executionArn) {
					setExecutionArn(res.data?.startMultiCloudwatch?.executionArn);
				} else {
					handleConfirm(false);
					setError("Failed to start remediation");
				}
			});
		},
	});

	const handleRefetch = useCallback(() => {
		refetch();
		setSelectAll(false);
		formik.setFieldValue("cloudwatchControls", []);
		setRemediating(false);
		setExecutionArn("");
	}, [refetch]);

	//Management Account Dropdown Handle
  const handleManagementAccount = useCallback((e: SelectChangeEvent<string>) => {
		const account = accounts.find((i: Account) => i.arn === e.target.value);
		if (account) {
			setManagementArn(account.arn);
			setRegion(account.defaultregion)
			handleRefetch();
		}
	}, [accounts]);

	const handleSNSTopicChange = useCallback(
		(e: SelectChangeEvent<string>) => {
			formik.setFieldValue("snsTopicArn", e.target.value);
		},
		[formik]
	);

	const handleOnCloudtrailChange = useCallback(
		(e: SelectChangeEvent<string>) => {
			formik.setFieldValue("cloudtrailArn", e.target.value);
			if (e.target.value === "create_new_cloudtrail") {
				// open create cloudtrail dialog
				formik.setFieldValue("loggroupArn", "use_sixpillars_loggroup");
			} else {
				const trail = formData.trails.find(
					(trail: Trail) => trail.trailArn === e.target.value
				);
				if (trail?.cloudwatchLogGroupArn) {
					formik.setFieldValue("loggroupArn", trail?.cloudwatchLogGroupArn);
				} else {
					formik.setFieldValue("loggroupArn", "use_sixpillars_loggroup");
				}
			}
		},
		[formik, formData.trails]
	);

	useEffect(() => {
		if ((Object.keys(findingStatuses).length === Object.values(MultiInputControls).length &&
			Object.values(findingStatuses).every(status => status === "PASSED")) || Object.values(findingStatuses).some(status => status === "No Findings")) {
			setDisabled(true)
		} else {
			setDisabled(false);
		}
	}, [findingStatuses]);


	useEffect(() => {
		formik.setSubmitting(false);
		formik.setErrors({});
		if (!loading && data?.getMultiCloudwatchState?.cloudTrails) {
			const trails = JSON.parse(data?.getMultiCloudwatchState?.cloudTrails);
			const topics = JSON.parse(data?.getMultiCloudwatchState?.snsTopics);
			setFormData({
				trails,
				topics,
			});
		}
	}, [data, loading]);

	useEffect(() => {
		if (remediationProgress === 100 && remediating) {
			handleConfirm(true);
			setRemediating(false);
		}
	}, [remediationProgress, handleConfirm]);

	return (
		<form onSubmit={formik.handleSubmit}>
		<Grid
			container
			spacing={2}
			alignItems="stretch"
		>
			<Grid container sx={{ m: 1 }}>
				<Grid item xs md={12}>
					<Typography
						component={"span"}
						variant="h6"
						color="text.primary"
						paragraph
					>
						Multi Cloudwatch Remediations
					</Typography>
				</Grid>	
				<Grid item xs md={12} sx={{pt:1, pb:1}}>
					<Alert severity="info">
						Please select the aws account you wish to remediate cloudwatch in. 
						If your account is part of aws organisations this would be the management account.
					</Alert>
				</Grid>
			</Grid>
			<Grid container sx={{ m: 1 }}>
				<Grid item md={6}>
					<Typography color="primary">
						Select AWS Account to implement Cloudwatch.1-14
					</Typography>
				</Grid>
				<Grid item md={6}>
					<FormControl fullWidth size="small">
						<InputLabel id="multi-input-cloudwatch-management-arn">
							AWS Account to implement Cloudwatch.1-14
						</InputLabel>
						<Select
							required
							id="multi-input-cloudwatch-management-arn-select"
							name="multi-input-cloudwatch-management-arn"
							value={managementArn}
							label="AWS Account to implement Cloudwatch.1-14"
							onChange={handleManagementAccount}
						>
							{accounts.length === 0 ? (
								<MenuItem disabled>No AWS accounts available</MenuItem>
							) : (
								accounts.map((a: Account) => (
									<MenuItem key={a.arn} value={a.arn}>
										{`${a.accessnickname} - ${a.arn.split(":")[4]}`}
									</MenuItem>
								))
							)}
						</Select>
					</FormControl>
				</Grid>
			</Grid>
			{!loading ? (
				<>
				<Grid container sx={{ m: 1 }}>
					<Grid item md={6}>
						<Typography color="primary">
							Email address{" "}
							<Tooltip title="You will receive an SNS topic subscription confirmation email. Please click the link in the email to confirm your subscription.">
								<IconButton>
									<InfoIcon fontSize="small" color="info" />
								</IconButton>
							</Tooltip>
						</Typography>
					</Grid>
					<Grid item md={6}>
						<FormControl fullWidth size="small">
							<TextField
								id="snsSubscriptionEmail"
								name="snsSubscriptionEmail"
								label="SNS Subscription Email"
								variant="outlined"
								size="small"
								fullWidth
								value={formik.values.snsSubscriptionEmail}
								onChange={formik.handleChange}
								error={
									formik.touched.snsSubscriptionEmail &&
									Boolean(formik.errors.snsSubscriptionEmail)
								}
								helperText={
									formik.touched.snsSubscriptionEmail &&
									formik.errors.snsSubscriptionEmail
								}
							/>
						</FormControl>
					</Grid>
				</Grid>
				<Grid container sx={{ m: 1 }}>
					<Grid item md={6}>
						<Typography color="primary">
							Multi region Cloudtrail{" "}
							<Tooltip title="Please select a multi-region CloudTrail to enable CloudWatch alarms. If you do not have a multi-region CloudTrail, you can create a new one. If your organization is set up with multiple accounts, select the CloudTrail deployed in the account where A+ is implemented.">
								<IconButton>
									<InfoIcon fontSize="small" color="info" />
								</IconButton>
							</Tooltip>
						</Typography>
					</Grid>
					<Grid item md={6}>
						<FormControl fullWidth size="small">
							<Select
								name="cloudtrailArn"
								value={formik.values.cloudtrailArn}
								fullWidth
								onChange={handleOnCloudtrailChange}
							>
								<MenuItem key="Select Cloudtrail" value={""}>
									Select Cloudtrail
								</MenuItem>
								{formData.trails.map((trail: Trail) => (
									<MenuItem
										key={trail.trailArn}
										value={trail.trailArn}
										sx={{ textOverflow: "ellipsis" }}
									>
										{trail.name}
									</MenuItem>
								))}
								{formData.trails.length === 1 &&
									!formData.trails[0]?.trailArn && (
										<MenuItem
											key={"create_new_cloudtrail"}
											value={"create_new_cloudtrail"}
										>
											Create New Cloudtrail
										</MenuItem>
									)}
							</Select>
						</FormControl>
					</Grid>
				</Grid>
				<Grid container sx={{ m: 1 }}>
					<Grid item md={6}>
						<Typography color="primary">
							Cloudwatch log group
							<Tooltip title="This log group will be used to create CloudWatch metrics and will be added to the selected CloudTrail.">
								<IconButton>
									<InfoIcon fontSize="small" color="info" />
								</IconButton>
							</Tooltip>
						</Typography>
					</Grid>
					<Grid item md={6}>
						<FormControl fullWidth size="small">
							<TextField
								id="loggroupArn"
								name="loggroupArn"
								variant="outlined"
								size="small"
								fullWidth
								value={formik.values.loggroupArn}
								disabled
							/>
						</FormControl>
					</Grid>
				</Grid>
				<Grid container sx={{ m: 1 }}>
					<Grid item md={6}>
						<Typography color="primary">
							SNS Topic ARN
							<Tooltip title="CloudWatch 1-14 requires an SNS topic to deliver CloudWatch alarms to users.">
								<IconButton>
									<InfoIcon fontSize="small" color="info" />
								</IconButton>
							</Tooltip>
						</Typography>
					</Grid>
					<Grid item md={6}>
						<FormControl fullWidth size="small">
							<Select
								id="snsTopicArn"
								name="snsTopicArn"
								value={formik.values.snsTopicArn}
								onChange={handleSNSTopicChange}
							>
								<MenuItem key="Select SNS Topic" value={""}>
									Select SNS Topic
								</MenuItem>
								{formData.topics.map((topic: Topic) => {
									return (
										<MenuItem key={topic.topicArn} value={topic.topicArn}>
											{topic.topicName}
										</MenuItem>
									);
								})}
								{
									<MenuItem
										key="create_new_sns_topic"
										value="use_sixpillars_topic"
									>
										Use SixPillars SNS Topic
									</MenuItem>
								}
							</Select>
						</FormControl>
					</Grid>
				</Grid>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell colSpan={4}>
								Please select the Cloudwatch controls you wish to enable
							</TableCell>
							<TableCell colSpan={8}>
								Select all enabled controls
								<Switch
									checked={selectAll}
									onChange={(e) => {
										const checked = e.target.checked;
										setSelectAll(checked);
										const failedControls = Object.entries(findingStatuses)
											.filter(([_, status]) => status === "FAILED")
											.map(([control]) => control);
										formik.setFieldValue(
											"cloudwatchControls",
											checked ? failedControls : []
										);
									}}
								/>
							</TableCell>
						</TableRow>
					</TableHead>
					<CloudwatchSwitches
						arn={managementArn}
	          region={region}
						formikCloudwatchControls={formik.values.cloudwatchControls}
						setFieldValue={formik.setFieldValue}
						setFindingStatuses={setFindingStatuses}
					/>
				</Table>
				{Object.values(findingStatuses).some(status => status === "No Findings") && (
					<Grid item xs md={12}>
						<Alert severity="error" sx={{ mt: 2 }}>
							Some CloudWatch controls show no findings. 
							This could be due to a relatively recent Security Hub activation (it takes around 24 hours to fully populate the status), 
							or because you're using an AWS Organization setup — in which case, 
							CloudWatch controls 1–14 should be enabled in the management account.
						</Alert>
					</Grid>
				)}
				{Object.values(formik.errors).map((error) => {
					return (
						<Alert key={error.toString()} severity="error">
							{error}
						</Alert>
					);
				})}
				{error !== "" && (
					<Alert
						severity="error"
						sx={{ display: error ? "block" : "none" }}
					>
						{error}
					</Alert>
				)}
				{(remediating || remediationProgress > 0) && (
					<Grid item xs md={12}>
						<Progress
							progress={remediationProgress}
							message={remediationMessage}
						/>
					</Grid>
				)}
				<Grid item xs md={12}>
					<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
						<Button 
							variant="outlined" 
							disabled={remediating}
							sx={{
								width: 300,
								height: 40,
								mr:4
							}} 
							onClick={changeTab}
						>
							Skip Remediation
						</Button>
						<LoadingButton
							variant="contained"
							color="secondary"
							type="submit"
							disabled={remediating || disabled}
							loading={remediating}
							sx={{
								width: 300,
								height: 40,
								bgcolor: "secondary.main",
								"&:hover": {
									bgcolor: "secondary.main",
									color: "secondary.contrastText",
								},
							}}
							loadingIndicator={
								<CircularProgress color={"secondary"} size={22} />
							}
						>
							Remediate CloudWatch Controls
						</LoadingButton>
					</Box>
				</Grid>
				</>
			):(
				<Grid item xs md={12}>
          <Typography>
            Please wait. We are analyzing your environment for optimal
            cloudwatch.1-14 remediation.
          </Typography>
          <Loader />
				</Grid>
			)}
		</Grid>	
		</form>	
	)
};

export default MultiInputCloudwatchRemediate;
